import { COMPANY_STATUS_LABELS } from '@l2u/ui/lib/companyStatus.ts';
import { FIELD_NOT_SET, type StatusFilter } from './companyFilter.ts';

/** The three free-text company-info fields the Vendors list filters on. */
export type VendorFilterField = 'type' | 'region' | 'station';

/**
 * What removing a chip should do, as data rather than a callback — so this
 * module stays pure (no router, no setters) and testable without a render.
 * Companies.tsx maps each variant onto the setter it already owns.
 */
export type VendorFilterChipRemoval =
  | { kind: 'query' }
  | { kind: 'status' }
  | { kind: 'field'; key: VendorFilterField; value: string };

export type VendorFilterChip = {
  /** Stable React key. Namespaced by field so two fields can share a value. */
  id: string;
  label: string;
  /** Display text — already resolved, including the not-set sentinel. */
  value: string;
  remove: VendorFilterChipRemoval;
};

export type VendorFilterChipInput = {
  query: string;
  status: StatusFilter;
  type: string[];
  region: string[];
  station: string[];
  /** Field labels, passed in so custom portal labels (companyInfoLabel) apply. */
  labels: Record<VendorFilterField, string>;
};

const NOT_SET_LABEL = '(Not set)';
const FIELDS: readonly VendorFilterField[] = ['type', 'region', 'station'];

/**
 * One chip per active filter on the Vendors list.
 *
 * This is the single definition of "the list is filtered": Companies.tsx sets
 * `filtersActive = chips.length > 0` rather than re-deriving it, so a filter
 * that narrows the table but produces no chip cannot exist. That combination
 * is what made a stale stored filter look like a data bug — the header said
 * "10 of 22" while no control appeared to be set and, with only one page of
 * results, the pager vanished too.
 *
 * Order matches the controls down the page: search, status pills, then the
 * three dropdowns left to right.
 */
export function vendorFilterChips(input: VendorFilterChipInput): VendorFilterChip[] {
  const chips: VendorFilterChip[] = [];

  // Trimmed, to agree with filterCompanies (which trims its query) and with
  // the old filtersActive test — a box holding only spaces filters nothing.
  const query = input.query.trim();
  if (query !== '') {
    chips.push({ id: 'q', label: 'Search', value: query, remove: { kind: 'query' } });
  }

  // 'all' is the absence of a status condition, not a status.
  if (input.status !== 'all') {
    chips.push({
      id: 'status',
      // COMPANY_STATUS_LABELS, never the enum value: 'expired' reads as
      // "Not Compliant" to admins and the chip must not leak the DB word.
      label: 'Status',
      value: COMPANY_STATUS_LABELS[input.status],
      remove: { kind: 'status' },
    });
  }

  for (const key of FIELDS) {
    for (const value of input[key]) {
      chips.push({
        id: `${key}:${value}`,
        label: input.labels[key],
        value: value === FIELD_NOT_SET ? NOT_SET_LABEL : value,
        // The sentinel, not its display text — this goes back into the filter.
        remove: { kind: 'field', key, value },
      });
    }
  }

  return chips;
}
